import { useEffect, useState } from 'react';
import { fetchRegistrationCount } from 'helpers/registrationApi';
import { REGISTRATIONS_CHANGED_EVENT } from 'helpers/registrationMeta';

// Refresh on window focus at most this often.
const FOCUS_REFRESH_INTERVAL_MS = 60 * 1000;

/**
 * Pending self-registration count for the sidebar badge (U3.6).
 *
 * Loads once when enabled, then stays in sync through the
 * REGISTRATIONS_CHANGED_EVENT the queue page broadcasts, plus a throttled
 * refresh when the window regains focus. Failures are silent — the badge is a
 * nicety, the queue page itself surfaces real errors.
 */
const usePendingRegistrationsCount = enabled => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return undefined;
    }

    let cancelled = false;
    let lastSyncedAt = 0;

    const load = async () => {
      lastSyncedAt = Date.now();
      try {
        const total = await fetchRegistrationCount('pending');
        if (!cancelled) {
          setCount(Math.max(0, Number(total) || 0));
        }
      } catch (error) {
        // silent on purpose
      }
    };

    const handleChanged = event => {
      const pending = event?.detail?.pending;
      if (typeof pending === 'number' && Number.isFinite(pending)) {
        lastSyncedAt = Date.now();
        setCount(Math.max(0, pending));
      } else {
        load();
      }
    };

    const handleFocus = () => {
      if (Date.now() - lastSyncedAt > FOCUS_REFRESH_INTERVAL_MS) {
        load();
      }
    };

    load();
    window.addEventListener(REGISTRATIONS_CHANGED_EVENT, handleChanged);
    window.addEventListener('focus', handleFocus);

    return () => {
      cancelled = true;
      window.removeEventListener(REGISTRATIONS_CHANGED_EVENT, handleChanged);
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled]);

  return count;
};

export default usePendingRegistrationsCount;
